import * as React from "react";
import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { MedicalTypeId, MedicalTypeIds } from "@teamprevent/common/healthSurveillance/models";

import { connect } from "react-redux";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { values } from "lodash";

import { ImageAnnotatorQuestion } from "@teamprevent/common/questionnaires/components/questions/imageAnnotatorQuestion/ImageAnnotatorQuestion";

import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";

import { HealthSurveillanceBodyImageUrl } from "../models";
import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

interface AdditionalProps {
    medicalTypes?: MedicalTypeId[];
}

type AllProps = CustomQuestionsQuestionnaireSectionProps & AdditionalProps;

// eslint-disable-next-line max-len
const HealthSurveillanceConsultationSkinAssessmentSectionUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const questions = values(props.questions || {});
    const assessmentRequiredAnswer = "Issue Identified - Skin Assessment Required";
    const assessmentNotRequiredAnswer = "No Issue Identified - Skin Assessment Not Required";
    const isSkinAssessmentLevel2 = props.medicalTypes?.some(m => m === MedicalTypeIds.SkinAssessmentLevel2) ?? false;

    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.SkinAssessment}
        >
            {!isSkinAssessmentLevel2 &&
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinAssessmentRequired)}
                    options={[assessmentNotRequiredAnswer, assessmentRequiredAnswer].map(a => ({ text: a, value: a }))}
                    mandatory
                />
            }
            {(getAnswerToQuestionWithId(questions, questionIds.SkinAssessmentRequired) === assessmentRequiredAnswer || isSkinAssessmentLevel2) && <>
                <ImageAnnotatorQuestion
                    imageHref={HealthSurveillanceBodyImageUrl}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AreasOfSkinExamined)}
                    allowNotes
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinInGoodCondition)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinCareAdviceProvided)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GlovesUseAdviceProvided)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightHandColour)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightHandConditionOfSkin)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightHandLesionsPresent)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftHandColour)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftHandConditionOfSkin)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftHandLesionsPresent)}
                    mandatory
                />
                <p>Any history of the following: (please tick all that apply)</p>
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryOfContactWithChemicals)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryOfContactWithOtherSubstances)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryOfPreviousAllergy)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TakingMedicationRegularlyAtPresent)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WorkingEnvironmentCateringCleaning)}
                    value="Yes"
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhetherConditionImprovesAwayFromWork)}
                    value="Yes"
                />
                <p>Is there any family history of the following:</p>
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FamilyHistoryOfAsthma)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FamilyHistoryOfHayFever)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FamilyHistoryOfEczema)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FamilyHistoryOfAnySkinCondition)}
                    mandatory
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HomeEnvironment)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.KnownSkinCondition)}
                    mandatory
                />
                {
                    getAnswerToQuestionWithId(questions, questionIds.KnownSkinCondition) === MultiButtonQuestion.YesOption &&
                    <>
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionName)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HowLongHadSkinCondition)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhoDiagnosedSkinCondition)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TakeMedicationForSkinCondition)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PartOfBodyAffectedBySkinCondition)}
                            mandatory
                        />
                        <p>How does it present?</p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionDryness)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionItching)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionRedness)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionScaling)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionCracking)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionFlaking)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionBlisters)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SkinConditionHowLongLast)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LastIssueWithSkinCondition)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NonMedicationImprovementOnSkinCondition)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AnyCauseDeteriorationToSkinCondition)}
                            mandatory
                        />
                        <p>Any History of the following</p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistorySwellingOfFaceHands)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryRednessOfFaceHands)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistorySneezingRunnyEyes)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryCoughShortnessOfBreath)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistoryLatexAllergyConfirmed)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HistorySkinConditionDetails)}
                            mandatory
                        />
                    </>
                }
            </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const HealthSurveillanceConsultationSkinAssessmentSection = connect(makeMapStateToQuestionnaireSectionProps(sections.SkinAssessment.id), mapDispatchToQuestionnaireSectionProps)(HealthSurveillanceConsultationSkinAssessmentSectionUnconnected);
